/**
 * Header molecule for the GO Lab Collection Detail page.
 *
 * Renders the collection's name, locale strip
 * (`region · industry · {taxonomy display name}`), a clickable status
 * pill (draft ↔ active), and a kebab menu for edit / delete. The
 * actions are flat callbacks so Storybook can fixture them without
 * mounting the parent page's mutation hooks.
 */

import React from 'react';
import { MoreHorizontal } from 'lucide-react';

import { Badge } from '../../../../../ui/badge';
import { Button } from '../../../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../ui/dropdown-menu';

interface Props {
  name: string;
  region: string;
  industry: string;
  taxonomyDisplayName: string;
  status: 'active' | 'draft';
  /** Fires when the status pill is clicked — page opens the status modal. */
  onStatusClick?: () => void;
  /** Fires when the kebab's "Edit details…" item is chosen. */
  onEdit?: () => void;
  /** Fires when the kebab's "Delete collection…" item is chosen. */
  onDelete?: () => void;
}

const CollectionHeader: React.FC<Props> = ({
  name,
  region,
  industry,
  taxonomyDisplayName,
  status,
  onStatusClick,
  onEdit,
  onDelete,
}) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div className="space-y-1">
      <h1
        className="font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground md:text-4xl"
        data-testid="collection-name"
      >
        {name}
      </h1>
      <p className="text-[13px] leading-[1.7] text-muted-foreground" data-testid="collection-locale">
        {region} · {industry} · {taxonomyDisplayName}
      </p>
    </div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onStatusClick}
        disabled={!onStatusClick}
        className="cursor-pointer rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-default"
        aria-label={
          status === 'active' ? 'Deactivate collection' : 'Activate collection'
        }
        data-testid="collection-status-button"
      >
        <Badge
          variant={status === 'active' ? 'default' : 'secondary'}
          className="capitalize"
          data-testid="collection-status-badge"
        >
          {status}
        </Badge>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Collection actions"
            data-testid="collection-actions-trigger"
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => onEdit?.()}
            data-testid="collection-actions-edit"
          >
            Edit details…
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => onDelete?.()}
            data-testid="collection-actions-delete"
          >
            Delete collection…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
);

export default CollectionHeader;
